import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _ from 'underscore';
import DateTimeField from 'react-datetime';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Label } from 'reactstrap';
import { kebabCase } from 'lodash';

/**
 *  Determine if additional offset is needed based on the current user's location
 *  and if that location supports DST
 */
const getTzInfo = incomingDate => {
  const currentTimeZone = moment.tz.guess();
  const currentDateLocalized = moment.tz(currentTimeZone);
  const incomingDateLocalized = moment.utc(incomingDate).tz(currentTimeZone);
  let dstAdjustment = 0;
  if (currentDateLocalized.isDST() && !incomingDateLocalized.isDST()) {
    // when local current time IS DST and local incoming time IS NOT, remove one hour
    dstAdjustment = -60;
  } else if (!currentDateLocalized.isDST() && incomingDateLocalized.isDST()) {
    // when local current time IS NOT DST and local incoming time IS DST, add one hour
    dstAdjustment = 60;
  }
  const timezoneOffset = moment.tz(moment.utc(), currentTimeZone).utcOffset();
  return {
    dstOffset: () => dstAdjustment,
    timezoneOffset
  };
};

/**
 *  Convert a date to local time
 */
const makeLocalDate = incomingDate => {
  const _getTzInfo = getTzInfo(incomingDate),
    dstOffset = _getTzInfo.dstOffset,
    timezoneOffset = _getTzInfo.timezoneOffset;
  const stillUtc = moment.utc(incomingDate).add(timezoneOffset, 'minutes').add(dstOffset(incomingDate), 'minutes');
  let date = incomingDate;
  if (_.isDate(date) || _.isString(date)) {
    date = stillUtc;
  } else {
    date = null;
  }
  if (date === null || typeof date === 'undefined' || !date.isValid()) {
    date = '';
  }
  return date;
};

/**
 *  Convert a date to UTC
 */
const makeUTCDate = incomingDate => {
  const _getTzInfo2 = getTzInfo(incomingDate),
    dstOffset = _getTzInfo2.dstOffset,
    timezoneOffset = _getTzInfo2.timezoneOffset;
  return moment.utc(incomingDate).subtract(timezoneOffset, 'minutes').subtract(dstOffset(incomingDate), 'minutes');
};

/**
 *  DateTimePicker
 *
 *  Display a react-datetime based on either `controlledValue`, which is set by user input
 *  from react-datetime, or `defaultValue`, which is determined by the parent component. In
 *  form contexts the value is pulled and set in Form.content. Unlike the react-datetime
 *  examples, this component does not use local state for date storage to avoid tricky edge
 *  cases and simplify the implementation.
 *
 *  This component converts display between local time on the user's browser and UTC, which
 *  is used for date storage up the component hierarchy.
 */

const DateTimePicker = _ref => {
  let _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$dateFormat = _ref.dateFormat,
    dateFormat = _ref$dateFormat === void 0 ? 'M/D/YYYY' : _ref$dateFormat,
    _ref$defaultValue = _ref.defaultValue,
    defaultValue = _ref$defaultValue === void 0 ? null : _ref$defaultValue,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$isValidDate = _ref.isValidDate,
    isValidDate = _ref$isValidDate === void 0 ? () => true : _ref$isValidDate,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$timeFormat = _ref.timeFormat,
    timeFormat = _ref$timeFormat === void 0 ? null : _ref$timeFormat,
    _ref$verticalPadding = _ref.verticalPadding,
    verticalPadding = _ref$verticalPadding === void 0 ? true : _ref$verticalPadding,
    _ref$viewMode = _ref.viewMode,
    viewMode = _ref$viewMode === void 0 ? null : _ref$viewMode,
    _ref$value = _ref.value,
    controlledValue = _ref$value === void 0 ? null : _ref$value,
    onChange = _ref.onChange,
    _ref$onOpen = _ref.onOpen,
    onOpen = _ref$onOpen === void 0 ? undefined : _ref$onOpen;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    entryDate = _useState2[0],
    setEntryDate = _useState2[1];

  // default to the partial entry date, otherwise falling back to props
  let tzAdjustedDate = entryDate;
  // when clearing the entry value in the UI tzAdjustedDate === ''
  if (tzAdjustedDate === null) {
    const value = controlledValue || defaultValue;
    tzAdjustedDate = makeLocalDate(value);
  }

  // track cursor moving when typing to avoid jumping to the end
  const inputElement = useRef(null);
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    cursorPosition = _useState4[0],
    setCursorPosition = _useState4[1];
  useEffect(() => {
    // only change selection if the element is already focused
    if (inputElement.current === global.document.activeElement) {
      inputElement.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [entryDate, cursorPosition]);
  const onInput = () => {
    var _inputElement$current;
    return setCursorPosition((_inputElement$current = inputElement.current) === null || _inputElement$current === void 0 ? void 0 : _inputElement$current.selectionStart);
  };

  /**
   *  Handle updates made by the user. If the date is valid this will be a moment
   *  object, otherwise a string
   */
  const handleChange = useCallback(val => {
    setEntryDate(val);
  }, []);

  /**
   *  When the picker closes, check for a valid entry date, and send it to onChange.
   *  onClose from react-datetime should return the current selection, but in my testing
   *  this wasn't getting updated.
   */
  const handleClose = useCallback(() => {
    // If the user only opened/closed the picker without selecting/typing,
    // do not emit a change that would clear an existing stored value.
    if (entryDate === null) return;

    // do not store date/time value as an empty string
    const newValue = moment.isMoment(entryDate) ? makeUTCDate(entryDate).toISOString() : null;
    onChange(newValue);
    setEntryDate(null); // clear the entry value so that the parent props.value will be used
  }, [entryDate, onChange]);

  /**
   * Handle when the picker opens (input gains focus)
   * If value is null/undefined and onOpen callback is provided, call it
   */
  const handleFocus = useCallback(() => {
    if (onOpen && (controlledValue === null || controlledValue === undefined) && (defaultValue === null || defaultValue === undefined)) {
      onOpen();
    }
  }, [controlledValue, defaultValue, onOpen]);
  const modifiedClassName = className.length > 0 ? className : "t-dtp-".concat(kebabCase(label));
  const displayLabel = label ? /*#__PURE__*/React.createElement(Label, null, label) : undefined;
  const disabledClassname = "".concat(modifiedClassName, "-disabled disabled");
  const classWrapper = classnames({
    'form-group': verticalPadding
  });
  const inputProps = {
    disabled,
    // cursor tracking
    onInput,
    ref: inputElement,
    // onOpen hook - call handleFocus when input gains focus
    onFocus: onOpen ? handleFocus : undefined
  };
  if (!tzAdjustedDate) {
    /**
     * This is a workaround to clear the input if the value
     * isn't truthy.  We can remove this once the problem with
     * react-datetime is resolved: https://github.com/arqex/react-datetime/issues/749
     * Pivotal Ticket: https://www.pivotaltracker.com/story/show/178814728
     */
    inputProps.value = '';
  }
  const id = "dtp-core-".concat(kebabCase(label));
  return /*#__PURE__*/React.createElement("div", {
    className: classWrapper
  }, displayLabel, /*#__PURE__*/React.createElement(DateTimeField, {
    "data-testid": id,
    inputProps: inputProps,
    enabled: false,
    className: "".concat(modifiedClassName, " ").concat(disabled ? disabledClassname : '', " t-dtp-input tqc-control"),
    value: tzAdjustedDate,
    timeFormat: timeFormat,
    dateFormat: dateFormat,
    onChange: handleChange,
    onClose: handleClose,
    viewMode: viewMode,
    isValidDate: isValidDate,
    utc: true
  }));
};
DateTimePicker.propTypes = {
  className: PropTypes.string,
  dateFormat: PropTypes.string,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  isValidDate: PropTypes.func,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  timeFormat: PropTypes.string,
  verticalPadding: PropTypes.bool,
  viewMode: PropTypes.string
};
export default DateTimePicker;